'use client';

import type { VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';
import { Check, Copy, X } from 'lucide-react';

const RESET_DELAY_MS = 1400;
const MASK_CHARACTER = '•';
const REVEALED_DIGITS = 4;
const NO_VALUE = '—';

const MASKS = {
  ssn: {
    valueName: 'SSN',
    redact: (tail: string) => `${MASK_CHARACTER.repeat(3)}-${MASK_CHARACTER.repeat(2)}-${tail}`,
  },
  'account-number': {
    valueName: 'account number',
    redact: (tail: string) => `${MASK_CHARACTER.repeat(REVEALED_DIGITS)}${tail}`,
  },
} as const;

export type MaskedValueMask = keyof typeof MASKS;

const rootVariants = cva('group/masked inline-flex max-w-full items-center text-left', {
  variants: {
    size: {
      default: 'gap-1.5 text-base',
      sm: 'gap-1 text-sm',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const iconVariants = cva('shrink-0', {
  variants: {
    size: {
      default: 'size-4',
      sm: 'size-3.5',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export type MaskedValueSize = NonNullable<VariantProps<typeof rootVariants>['size']>;

const FACE_CLASSES =
  'col-start-1 row-start-1 select-none whitespace-nowrap transition-[opacity,filter] duration-200 ease-out motion-reduce:transition-none';

// `:active` is the only reveal a touch device can reach: `group-hover` compiles behind
// `@media (hover: hover)` and a tap never matches `:focus-visible`.
const HIDE_ON_REVEAL =
  'group-hover/masked:opacity-0 group-hover/masked:blur-xs group-focus-visible/masked:opacity-0 group-focus-visible/masked:blur-xs group-active/masked:opacity-0 group-active/masked:blur-xs';
const SHOW_ON_REVEAL =
  'group-hover/masked:opacity-100 group-hover/masked:blur-none group-focus-visible/masked:opacity-100 group-focus-visible/masked:blur-none group-active/masked:opacity-100 group-active/masked:blur-none';
const SHOW_ICON_ON_REVEAL =
  'group-hover/masked:opacity-100 group-focus-visible/masked:opacity-100 group-active/masked:opacity-100';

// A short or malformed value must not leak through the mask.
export function maskValue(value: string, mask: MaskedValueMask): string {
  const suffix = value.replace(/\D/gu, '').slice(-REVEALED_DIGITS);
  const tail = suffix.length === REVEALED_DIGITS ? suffix : MASK_CHARACTER.repeat(REVEALED_DIGITS);
  return MASKS[mask].redact(tail);
}

type CopyStatus = 'idle' | 'copied' | 'failed';

export type MaskedValueProps = {
  readonly value: string;
  readonly mask?: MaskedValueMask;
  readonly valueName?: string;
  readonly size?: MaskedValueSize;
  readonly icon?: LucideIcon;
  readonly className?: string;
};

export function MaskedValue({
  value,
  mask = 'account-number',
  valueName = MASKS[mask].valueName,
  size,
  icon: Icon,
  className,
}: MaskedValueProps) {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const [attempt, setAttempt] = useState(0);
  // Detected after mount so the server and first client renders agree, then upgrade.
  const [canCopy, setCanCopy] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCanCopy(typeof navigator.clipboard?.writeText === 'function');
  }, []);

  useEffect(
    () => () => {
      if (resetTimer.current != null) clearTimeout(resetTimer.current);
    },
    [],
  );

  async function copy() {
    let copied = true;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      copied = false;
    }
    setStatus(copied ? 'copied' : 'failed');
    setAttempt((previous) => previous + 1);
    if (resetTimer.current != null) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setStatus('idle'), RESET_DELAY_MS);
  }

  const digits = value.replace(/\D/gu, '');
  const last4 = digits.slice(-REVEALED_DIGITS);
  const glyph = Icon ? <Icon className={cn(iconVariants({ size }), 'text-secondary')} aria-hidden="true" /> : null;

  if (digits === '') {
    return (
      <span className={cn(rootVariants({ size }), 'text-secondary', className)}>
        {glyph}
        {NO_VALUE}
      </span>
    );
  }

  const faces = (
    <>
      {glyph}
      {/* One grid cell for both faces, so the box sizes to the wider one and reveal can't clip. */}
      <span className="inline-grid tabular-nums text-dark">
        <span className={cn(FACE_CLASSES, HIDE_ON_REVEAL)}>{maskValue(value, mask)}</span>
        {/* Hidden from AT on purpose — reading a full SSN aloud is the leak the mask prevents.
            The last four reach AT through the button's name. */}
        <span aria-hidden="true" className={cn(FACE_CLASSES, 'opacity-0 blur-xs', SHOW_ON_REVEAL)}>
          {value}
        </span>
      </span>
    </>
  );

  if (!canCopy) {
    return <span className={cn(rootVariants({ size }), className)}>{faces}</span>;
  }

  const announcement =
    status === 'copied' ? `${valueName} copied` : status === 'failed' ? `Couldn't copy ${valueName}` : '';

  return (
    <>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={last4.length === REVEALED_DIGITS ? `Copy ${valueName}, ending in ${last4}` : `Copy ${valueName}`}
        className={cn(
          rootVariants({ size }),
          'cursor-pointer rounded-sm focus-visible:ring-2 focus-visible:ring-border-light focus-visible:outline-none',
          className,
        )}
      >
        {faces}
        {status === 'copied' ? (
          <Check className={cn(iconVariants({ size }), 'text-primary')} aria-hidden="true" />
        ) : status === 'failed' ? (
          <X className={cn(iconVariants({ size }), 'text-destructive')} aria-hidden="true" />
        ) : (
          <Copy
            className={cn(
              iconVariants({ size }),
              'text-secondary opacity-0 transition-opacity motion-reduce:transition-none',
              SHOW_ICON_ON_REVEAL,
            )}
            aria-hidden="true"
          />
        )}
      </button>
      {/* The region stays mounted and the message swaps inside it, so a repeat copy inside the
          reset window still mutates a pre-existing live region and gets announced. */}
      <span role="status" aria-live="polite" className="sr-only">
        <span key={attempt}>{announcement}</span>
      </span>
    </>
  );
}
